import { useState } from "react";
import { useMediaQuery } from "@mui/material";
import {
  M_CheckBox,
  M_DrawerTableTextField,
  M_InputTextField,
  M_Select,
  M_TextArea,
} from "src/M_Components";
import { revisionHighlightColors } from "../utils/quoteRevisionHelpers";

export const changes = (field, value, setData) => {
  setData((prev) => ({ ...prev, [field]: value }));
};

export const getLoggedUserEmail = () => {
  const fromStorage = localStorage.getItem("accountEmail");
  if (fromStorage) return fromStorage;

  try {
    const token = localStorage.getItem("tokenMaeil");
    if (!token) return "";

    const base64 = token.split(".")[1]?.replace(/-/g, "+").replace(/_/g, "/");
    if (!base64) return "";

    const payload = JSON.parse(atob(base64));
    return (
      payload.email ||
      payload.Email ||
      payload.sub ||
      payload.unique_name ||
      ""
    );
  } catch {
    return "";
  }
};

export const createdByField = (t) => ({
  display: t("CreatedBy"),
  code: "createdBy",
  type: "disabled",
  size: "380px",
  style: {
    "& .MuiOutlinedInput-input": { fontSize: "15px", padding: "10px 14px" },
    "& label": { fontSize: "14px" },
  },
});

export const QuoteFormField = ({
  fields,
  index,
  data,
  setData,
  hideLabels,
  returnFunc,
  size,
  options,
  margin,
  noAutocomplete,
  submit,
  cancel,
  toggleable,
  disabled,
  revisionContext,
}) => {
  const [isDisabled, setIsDisabled] = useState(
    disabled ?? toggleable ?? false,
  );

  const matches5 = useMediaQuery("(min-width:1480px)");

  const getFieldColors = (fieldCode) =>
    revisionHighlightColors(
      data,
      revisionContext?.section ?? null,
      revisionContext?.index ?? null,
      fieldCode,
      fields?.required && !data?.[fieldCode],
    );

  return (
    <div
      style={{
        display: "flex",
        width: fields?.fullWidth ? "100%" : "auto",
        justifyContent: "flex-start",
      }}
    >
      {(fields.type !== "disabled" || !matches5) &&
        fields.type !== "whitespace" &&
        fields.type && // to prevent whitespace checks from triggering it
        !disabled &&
        toggleable && (
          <div
            style={{
              // show the field even though not useable,
              // just so they can be aligned with the ones that have checkboxes
              visibility:
                fields.type === "disabled" && !matches5 ? "hidden" : "visible",
            }}
          >
            <M_CheckBox
              checked={!isDisabled}
              onChange={(e) => setIsDisabled(!e.target.checked)}
            />
          </div>
        )}

      {(fields.type === "disabled" || isDisabled) && (
        <div style={{ margin: "5px", width: "auto" }}>
          <M_InputTextField
            translate={fields?.translate}
            size={(size && size) || fields?.size || "250px"}
            style={fields?.style}
            disabled
            label={hideLabels ? null : fields.display}
            value={data[fields.code] || ""}
            favoritesFieldName={fields?.favoritesFieldName}
            favoritesGroupName={fields?.favoritesGroupName}
            favoritesVarKey={fields?.favoritesVarKey}
            {...getFieldColors(fields.code)}
          />
        </div>
      )}
      {fields.type === "select" && !isDisabled && (
        <div style={{ margin: "5px" }}>
          <M_Select
            translate={fields?.translate}
            margin={margin}
            autocomplete={!noAutocomplete && true}
            hasNone
            size={(size && size) || "250px"}
            label={hideLabels ? null : fields.display}
            value={
              (data &&
                data[fields.code] && typeof data[fields.code] === "string" && {
                  label: data[fields.code],
                  value:
                    (data[fields?.extraField] && data[fields?.extraField]) ||
                    data[fields.code],
                }) ||
              (data &&
                data[fields.code]&& {
                  label: data[fields.code].label,
                  value:
                    (data[fields?.extraField] && data[fields?.extraField]) ||
                    data[fields.code],
                }) ||
              null
            }
            options={
              (data && data[fields?.options]) || (options && options) || []
            }
            onChangeValue={(obj) => {
              if (returnFunc) {
                returnFunc(data, fields, obj);
                return;
              }
              changes(fields.code, (obj && obj.label) || "", setData);
              if (fields?.extraField) {
                changes(fields.extraField, (obj && obj.value) || "", setData);
              }
            }}
            required={
              hideLabels
                ? null
                : (fields?.required && fields?.required) || false
            }
            {...getFieldColors(fields.code)}
            disabled={isDisabled}
            favoritesFieldName={fields?.favoritesFieldName}
            favoritesGroupName={fields?.favoritesGroupName}
            favoritesIdKey={fields?.favoritesIdKey}
            favoritesVarKey={fields?.favoritesVarKey}
          />
        </div>
      )}

      {fields.type === "foreignKey" && !isDisabled && (
        <div style={{ margin: "5px", width: "auto" }}>
          <M_DrawerTableTextField
            translate={fields?.translate}
            size={(size && size) || "250px"}
            style={{ maxWidth: "250px" }}
            tableLabel={fields?.tableLabel ?? fields?.entity}
            label={hideLabels ? null : fields?.display}
            entity={fields?.entity}
            FILTER={fields?.FILTER}
            filterField={fields?.filterField}
            labelFields={fields?.labelFields}
            FILTER={fields?.FILTER}
            value={
              data && data[fields.code]
                ? Object.assign(
                    {},
                    ...fields.fields.map((obj) => ({
                      [obj.key]: data[obj.value],
                    })),
                  )
                : null
            }
            onSubmitValue={(obj) => {
              if (returnFunc) {
                returnFunc(data, fields, obj);
                return;
              }
              fields.fields?.forEach((keyValue) => {
                changes(
                  keyValue.value,
                  obj?.[keyValue.key] ?? null,
                  setData,
                );
              });
            }}
            required={
              hideLabels
                ? null
                : (fields?.required && fields?.required) || false
            }
            {...getFieldColors(fields.code)}
            disabled={disabled}
            favoritesFieldName={fields?.favoritesFieldName}
            favoritesGroupName={fields?.favoritesGroupName}
            favoritesIdKey={fields?.favoritesIdKey}
            favoritesVarKey={fields?.favoritesVarKey}
          />
        </div>
      )}

      {(fields.type === "date" ||
        fields.type === "number" ||
        fields.type === "text") &&
        !isDisabled && (
          <div style={{ margin: "5px", minWidth: "50px" }}>
            <M_InputTextField
              translate={fields?.translate}
              size={(size && size) || "250px"}
              label={hideLabels ? null : fields.display}
              type={fields.type}
              value={(data && data[fields?.code]) || ""}
              required={
                hideLabels
                  ? null
                  : (fields?.required && fields?.required) || false
              }
              {...getFieldColors(fields.code)}
              onChange={(e) => {
                if (returnFunc) {
                  returnFunc(data, fields, e.target.value);
                  return;
                }
                changes(fields.code, e.target.value, setData);
              }}
              submit={submit}
              cancel={cancel}
              disabled={disabled}
              favoritesFieldName={fields?.favoritesFieldName}
              favoritesGroupName={fields?.favoritesGroupName}
              favoritesVarKey={fields?.favoritesVarKey}
            />
          </div>
        )}

      {fields.type === "textArea" && !isDisabled && (
        <div
          style={{
            margin: "5px",
            minWidth: "50px",
            width: fields?.fullWidth ? "100%" : "auto",
            display: "flex",
            flexDirection: "column",
            flex: fields?.fillHeight ? 1 : undefined,
            minHeight: fields?.fillHeight ? 0 : undefined,
            justifyContent: "flex-start",
          }}
        >
          <M_TextArea
            translate={fields?.translate}
            size={fields?.size || (size && size) || "250px"}
            rows={fields?.fillHeight ? 3 : (fields?.rows || 2)}
            fillHeight={fields?.fillHeight}
            label={hideLabels ? null : fields.display}
            type={fields.type}
            value={data[fields.code] || ""}
            required={
              hideLabels
                ? null
                : (fields?.required && fields?.required) || false
            }
            {...getFieldColors(fields.code)}
            onChange={(e) => {
              if (returnFunc) {
                returnFunc(data, fields, e.target.value);
                return;
              }
              changes(fields.code, e.target.value, setData);
            }}
          />
        </div>
      )}

      {fields.type === "checkBox" && !isDisabled && (
        <div style={{ margin: "5px", width: "auto" }}>
          <M_CheckBox
            translate={fields?.translate}
            required={
              hideLabels
                ? null
                : (fields?.required && fields?.required) || false
            }
            label={hideLabels ? null : fields.display}
            checked={data[fields.code] || false}
            onChange={(e) => {
              if (returnFunc) {
                returnFunc(data, fields, e.target.checked);
                return;
              }
              changes(fields.code, e.target.checked, setData);
            }}
          />
        </div>
      )}

      {fields.type === "whitespace" && !isDisabled && (
        <div style={{ margin: "5px" }}></div>
      )}
    </div>
  );
};
